import { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { db, Product } from "../database/db";
import { useSettings } from "../contexts/SettingsContext";

type ExportType = "products-only" | "all-data";

export type ExportProduct = Omit<Product, "imageBlob" | "imageMimeType"> & {
  imageFilename?: string;
  imageMimeType?: string;
};

export default function DataExport() {
  const [exportType, setExportType] = useState<ExportType>("products-only");
  const [isExporting, setIsExporting] = useState(false);
  const { settings } = useSettings();

  const handleExport = async () => {
    setIsExporting(true);
    const zip = new JSZip();
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");

    try {
      // Export products
      const products = await db.products.toArray();
      const productsWithoutBlobs = await Promise.all(
        products.map(async (product) => {
          const { imageBlob, imageMimeType, ...productWithoutBlob } = product;

          // If product has an image, save it to the zip
          if (imageBlob && imageMimeType) {
            const extension = imageMimeType.split("/")[1] || "jpg";
            const filename = `images/product-${product.id}.${extension}`;
            zip.file(filename, imageBlob);
            return {
              ...productWithoutBlob,
              imageFilename: filename,
              imageMimeType,
            } as ExportProduct;
          }

          return productWithoutBlob as ExportProduct;
        })
      );

      // Add products to zip
      zip.file("products.json", JSON.stringify(productsWithoutBlobs, null, 2));

      // If exporting all data, add sales, bundles and settings
      if (exportType === "all-data") {
        const sales = await db.sales.toArray();
        const bundles = await db.bundles.toArray();

        zip.file("sales.json", JSON.stringify(sales, null, 2));
        zip.file("bundles.json", JSON.stringify(bundles, null, 2));
        zip.file("settings.json", JSON.stringify(settings, null, 2));
      }

      // Generate and download the zip
      const content = await zip.generateAsync({ type: "blob" });
      const filename = `conventionpos-export-${exportType}-${timestamp}.zip`;
      saveAs(content, filename);
    } catch (error) {
      console.error("Export failed:", error);
      // You might want to add error handling UI here
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Export Data
      </Typography>

      <Typography variant="body2" color="text.secondary" paragraph>
        Use this feature to create backups of your data. For detailed sales
        reports, please use the Reports page instead. The exported file will be
        downloaded as a ZIP archive containing your data in JSON format.
      </Typography>

      <RadioGroup
        value={exportType}
        onChange={(e) => setExportType(e.target.value as ExportType)}
        sx={{ mb: 2 }}>
        <FormControlLabel
          value="products-only"
          control={<Radio />}
          label="Products only (including images)"
        />
        <FormControlLabel
          value="all-data"
          control={<Radio />}
          label="All data (products, sales, and settings)"
        />
      </RadioGroup>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          variant="contained"
          startIcon={
            isExporting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <FileDownloadIcon />
            )
          }
          onClick={handleExport}
          disabled={isExporting}>
          {isExporting ? "Preparing Export..." : "Export Data"}
        </Button>
      </Box>
    </Paper>
  );
}
